import React from "react";

import { Grid, MenuItem, SelectChangeEvent } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { Select } from "../../../../../../../../../../../../components/Form";
import SkeletonComponent from "../../../../../../../../../../../../components/Generic/Skeleton";
import { useAppState } from "../../../../../../../../../../../../context/StateContext";
import useYupValidation from "../../../../../../../../../../../../hooks/useYupValidation";
import { TaskType } from "../../../../../../../../../../../../models/task.type";
import TasksTypesService from "../../../../../../../../../../../../services/task.type.service";
import { CACHE_TIME, STALE_TIME } from "../../../../../../../../../../../../util/constants";
import { Status } from "../../../../../../../../../../../../util/enums";
import { TaskTypeKeys } from "../../../../../../../../../../../../util/queries";
import { useActions } from "../../../../../hooks/actions";
import { TimesheetForm } from "../TimesheetForm";
import { Wrapper } from "./styles";

type CreatingMockTimesheetProps = {
  isLoading: boolean;
  isOnView?: boolean;
};

export const CreatingMockTimesheet: React.FC<CreatingMockTimesheetProps> = ({
  isLoading,
  isOnView = false,
}) => {
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const { timesheets, setTimesheets, timesheetErrors } = useActions();
  const { fieldHasError, getErrorByField } = useYupValidation(timesheetErrors);

  const { data, isFetching } = useQuery({
    queryKey: TaskTypeKeys.getAll([]),
    queryFn: async () => {
      const response = await TasksTypesService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
      });

      if (response?.data?.length) return response.data as TaskType[];

      return [];
    },
    refetchOnWindowFocus: false,
    staleTime: STALE_TIME,
    cacheTime: CACHE_TIME,
  });

  return (
    <Wrapper>
      <Grid container spacing={2} xs={12}>
        <Grid item xs={12} md={6}>
          <SkeletonComponent isLoading={isFetching || isLoading}>
            <Select
              label={translate("task")}
              value={timesheets?.taskType}
              onChange={(event: SelectChangeEvent<any>) =>
                setTimesheets({ ...(timesheets || {}), taskType: event.target.value })
              }
              error={fieldHasError("taskType")}
              errorMessage={getErrorByField("taskType")}
              disabled={isOnView || isLoading}
              required
            >
              {data?.map((task) => (
                <MenuItem key={task?.id} value={task?.name}>
                  {task?.name}
                </MenuItem>
              ))}
            </Select>
          </SkeletonComponent>
        </Grid>
      </Grid>
      <TimesheetForm isOnView={isOnView} isLoading={isLoading} />
    </Wrapper>
  );
};
